/**
 * Lenavio Watchdog — alerts dashboard (text-only).
 *
 * Platform-admin surface. Rich charts will land in a separate
 * "/admin/ops/dashboard" page; for now this is a structured table
 * + run-now button + acknowledgement flow so the watchdog is
 * useful from day one.
 *
 * Layout:
 *   ┌─ Health summary (open pages / warns / infos) ──┐
 *   ├─ Filter pills + Run-now button ────────────────┤
 *   └─ Alert list (severity badge, title, summary,
 *      affected orgs, action chip, ack button) ──────┘
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Play,
  RefreshCw,
} from "lucide-react";

import {
  ackOpsAlert,
  getOpsHealth,
  listOpsAlerts,
  runOpsWatchdog,
  type OpsAlertRow,
  type OpsHealth,
  type OpsSeverity,
} from "@/api/adminOps";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

type SeverityFilter = "all" | OpsSeverity;
type AckFilter = "all" | "open" | "acknowledged";

function SeverityBadge({ severity }: { severity: OpsSeverity }) {
  if (severity === "page")
    return (
      <Badge variant="danger">
        <AlertCircle className="mr-1 h-3 w-3" /> Page
      </Badge>
    );
  if (severity === "warn")
    return (
      <Badge variant="warning">
        <AlertTriangle className="mr-1 h-3 w-3" /> Warn
      </Badge>
    );
  return (
    <Badge variant="info">
      <Info className="mr-1 h-3 w-3" /> Info
    </Badge>
  );
}

function HealthPill({ health }: { health: OpsHealth | null }) {
  if (!health)
    return <Badge variant="default">…</Badge>;
  if (health.severity === "down")
    return <Badge variant="danger">Down — {health.open_pages} open</Badge>;
  if (health.severity === "degraded")
    return <Badge variant="warning">Degraded — {health.open_warns} open</Badge>;
  return <Badge variant="success">All systems operational</Badge>;
}

export function OpsAlertsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [alerts, setAlerts] = useState<OpsAlertRow[]>([]);
  const [health, setHealth] = useState<OpsHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busyAck, setBusyAck] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [ackFilter, setAckFilter] = useState<AckFilter>("open");

  const isPlatformAdmin = Boolean(user?.is_platform_admin);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, h] = await Promise.all([
        listOpsAlerts({
          severity:
            severityFilter === "all" ? undefined : severityFilter,
          acknowledged:
            ackFilter === "all"
              ? undefined
              : ackFilter === "acknowledged",
        }),
        getOpsHealth(),
      ]);
      setAlerts(rows);
      setHealth(h);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not load ops alerts.";
      addToast("error", detail);
    } finally {
      setLoading(false);
    }
  }, [severityFilter, ackFilter, addToast]);

  useEffect(() => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh, isPlatformAdmin]);

  if (!isPlatformAdmin) {
    return (
      <Card className="p-10 text-center text-sm text-text-muted">
        This page is restricted to platform administrators.
      </Card>
    );
  }

  async function onRunNow() {
    setRunning(true);
    try {
      const r = await runOpsWatchdog();
      addToast(
        "success",
        `Watchdog tick: ${r.fired} new alert(s)${r.paged ? `, ${r.paged} paged` : ""}.`,
      );
      void refresh();
    } catch {
      addToast("error", "Watchdog run failed.");
    } finally {
      setRunning(false);
    }
  }

  async function onAck(alert: OpsAlertRow) {
    setBusyAck(alert.id);
    try {
      const updated = await ackOpsAlert(alert.id);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alert.id ? updated : a)),
      );
      addToast("success", "Acknowledged");
    } catch {
      addToast("error", "Could not acknowledge");
    } finally {
      setBusyAck(null);
    }
  }

  const counts = useMemo(() => {
    const c = { page: 0, warn: 0, info: 0 };
    for (const a of alerts) c[a.severity] += 1;
    return c;
  }, [alerts]);

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Ops alerts"
        subtitle="Platform-wide watchdog signals across every tenant."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={onRunNow} disabled={running}>
              {running ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run watchdog now
            </Button>
          </div>
        }
      />

      {/* Health row */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <HealthPill health={health} />
            {health && (
              <span className="text-xs text-text-muted">
                {health.last_alert_at
                  ? `Last alert ${new Date(health.last_alert_at).toLocaleString()}`
                  : "No alerts on record"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 text-danger" />
              <span className="font-semibold tabular-nums">
                {health?.open_pages ?? 0}
              </span>
              <span className="text-text-muted">open page</span>
            </span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
              <span className="font-semibold tabular-nums">
                {health?.open_warns ?? 0}
              </span>
              <span className="text-text-muted">warn</span>
            </span>
            <span className="flex items-center gap-1">
              <Info className="h-3.5 w-3.5 text-accent" />
              <span className="font-semibold tabular-nums">
                {health?.open_infos ?? 0}
              </span>
              <span className="text-text-muted">info</span>
            </span>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-text-muted">
          Severity
        </span>
        {(["all", "page", "warn", "info"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSeverityFilter(s)}
            className={`rounded-full border px-3 py-1 text-xs capitalize ${
              severityFilter === s
                ? "border-accent bg-accent/10 text-accent"
                : "border-border text-text-muted hover:text-text-primary"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="ml-3 text-xs uppercase tracking-wide text-text-muted">
          State
        </span>
        {(["open", "acknowledged", "all"] as const).map((a) => (
          <button
            key={a}
            onClick={() => setAckFilter(a)}
            className={`rounded-full border px-3 py-1 text-xs capitalize ${
              ackFilter === a
                ? "border-accent bg-accent/10 text-accent"
                : "border-border text-text-muted hover:text-text-primary"
            }`}
          >
            {a}
          </button>
        ))}
      </div>

      {/* Alert list */}
      {loading ? (
        <Card className="flex h-32 items-center justify-center text-text-muted">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
        </Card>
      ) : alerts.length === 0 ? (
        <Card className="p-10 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-success" />
          <p className="text-sm font-medium">No alerts match the filter.</p>
          <p className="mt-1 text-xs text-text-muted">
            Either the platform's healthy or you've acknowledged everything.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-text-muted">
            {counts.page > 0 && `${counts.page} page · `}
            {counts.warn > 0 && `${counts.warn} warn · `}
            {counts.info > 0 && `${counts.info} info · `}
            showing {alerts.length}
          </p>
          {alerts.map((a) => (
            <Card
              key={a.id}
              className={`p-4 ${
                a.severity === "page"
                  ? "border-danger/40"
                  : a.severity === "warn"
                    ? "border-warning/40"
                    : ""
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <SeverityBadge severity={a.severity} />
                    <h3 className="text-sm font-semibold">{a.title}</h3>
                    <code className="text-[11px] text-text-muted">
                      {a.signal_key}
                    </code>
                    {a.acknowledged && (
                      <Badge variant="default">Acknowledged</Badge>
                    )}
                    {a.fanout_sent && (
                      <Badge variant="info">Paged</Badge>
                    )}
                  </div>
                  {a.summary && (
                    <p className="mb-2 text-sm">{a.summary}</p>
                  )}
                  {a.suggested_action && (
                    <p className="mb-2 text-sm text-accent">
                      → {a.suggested_action}
                    </p>
                  )}
                  {a.affected_orgs && a.affected_orgs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {a.affected_orgs.map((o, i) => (
                        <span
                          key={i}
                          className="rounded-md border border-border bg-surface-muted px-2 py-0.5 text-xs"
                        >
                          {o.name || o.slug || o.id}
                          {o.metric ? ` · ${o.metric}` : null}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-text-muted">
                    {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
                {!a.acknowledged && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busyAck === a.id}
                    onClick={() => onAck(a)}
                  >
                    {busyAck === a.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Acknowledge
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[11px] text-text-muted">
        The watchdog auto-runs every 15 minutes. Page-severity alerts also
        ping the founder Telegram bot + email. A richer visual dashboard
        with charts is planned next.
      </p>
    </motion.div>
  );
}

export default OpsAlertsPage;
